import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Download,
  Filter,
  ListPlus,
  Plus,
  Shield,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  CompanyStatuses,
  SecurityRiskLevels,
  formatNumber,
  formatShortDate,
  messages,
  type CompanySummary,
} from '@nesc/shared';
import { api, errorMessage, type ListResponse, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { useDebounced, useSelection, useUrlFilters } from '@/lib/hooks';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { ResponsiveList, RowChevron, type Column } from '@/components/common/responsive-list';
import { StatusBadge } from '@/components/common/status-badge';
import { ConfidenceMark } from '@/components/common/evidence';
import { EmptyState } from '@/components/common/feedback';
import { AppliedChip, BulkBar, FilterChips, PageHeader } from '@/components/common/page';
import { CompanyFilterSheet } from '@/features/companies/filter-sheet';
import { BulkAssignDialog, BulkTagDialog, BulkExcludeDialog } from '@/features/companies/bulk-dialogs';
import { CampaignListDialog } from '@/features/companies/campaign-list-dialog';
import { CompanyCreateDialog } from '@/features/companies/create-dialog';
import { CsvImportDialog } from '@/features/companies/csv-import-dialog';
import { ContactIcons } from '@/features/companies/contact-icons';
import { AddToListDialog } from '@/features/collections/add-to-list-dialog';

const DEFAULT_FILTERS = {
  q: '',
  status: '',
  assigneeId: '',
  tagIds: '',
  prefecture: '',
  industry: '',
  hasEmail: '',
  hasForm: '',
  hasPhone: '',
  hasAnyContact: '',
  securityRisk: '',
  securityChecked: '',
  excluded: '',
  listId: '',
  lowConfidenceOnly: '',
  lastContactedBefore: '',
  createdAfter: '',
  sort: '-updatedAt',
};

/** S30 企業一覧。PC は表、スマホはカード */
export const CompaniesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const { filters, setFilter, setFilters, reset, activeCount } = useUrlFilters(DEFAULT_FILTERS);
  const selection = useSelection();

  const [searchInput, setSearchInput] = useState(filters.q as string);
  const debouncedSearch = useDebounced(searchInput, 300);
  const [filterOpen, setFilterOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkDialog, setBulkDialog] = useState<'assign' | 'tags' | 'exclude' | null>(null);
  const [campaignListOpen, setCampaignListOpen] = useState(false);
  const [addToListOpen, setAddToListOpen] = useState(false);
  // 検索語も URL に持ち、戻る・共有で復元できるようにする（docs/08-uiux-design.md U08）
  const syncedSearch = useRef(debouncedSearch);
  useEffect(() => {
    if (debouncedSearch === syncedSearch.current) return;
    syncedSearch.current = debouncedSearch;
    setFilter('q', debouncedSearch);
  }, [debouncedSearch, setFilter]);

  const query = { ...filters, q: debouncedSearch, limit: 50 };

  // ページを react-query に持たせる。企業詳細から戻ったときに、読み込み済みのページごと即座に復元される
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: qk.companies(query),
    queryFn: ({ pageParam }) =>
      api.get<ListResponse<CompanySummary>>('/companies', { ...query, cursor: pageParam ?? undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.meta.hasMore ? last.meta.nextCursor : null),
  });

  const rows = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);
  const totalCount = data?.pages[0]?.meta.totalCount;

  const { data: assignees } = useQuery({
    queryKey: qk.assignableUsers,
    queryFn: () =>
      api.get<SingleResponse<{ id: string; name: string | null; email: string }[]>>('/users/assignable'),
  });

  const { data: tags } = useQuery({
    queryKey: qk.tags,
    queryFn: () => api.get<SingleResponse<{ id: string; name: string }[]>>('/tags'),
  });

  // 絞り込み条件（空でないもの）。CSV 出力と条件リストの作成で同じものを使う
  const activeFilter = Object.fromEntries(
    Object.entries(filters)
      .filter(([key, value]) => value && key !== 'sort' && key !== 'q')
      .map(([key, value]) => [key, String(value)]),
  );
  const filterForList = { ...activeFilter, ...(debouncedSearch ? { q: debouncedSearch } : {}) };

  const exportCsv = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<{ exportId: string; rowCount: number }>>('/exports/companies', {
        filter: activeFilter,
        q: debouncedSearch || undefined,
      }),
    onSuccess: (result) => {
      toast.success('CSV の生成を開始しました', {
        description: `${formatNumber(result.data.rowCount)} 行。完了すると通知でお知らせします`,
      });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const bulkSecurityScan = useMutation({
    mutationFn: (companyIds: string[]) =>
      api.post<SingleResponse<{ jobRunId: string; requested: number; skipped: number }>>(
        '/companies/bulk/security-scan',
        { companyIds },
      ),
    onSuccess: (result) => {
      toast.success(messages.company.securityBulkStarted(result.data.requested), {
        description:
          result.data.skipped > 0
            ? `URL 未設定の ${result.data.skipped} 社はスキップしました。完了は「処理」画面で確認できます`
            : '完了は「処理」画面で確認できます',
      });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  /** 条件も検索語も消す。検索語は URL 経由で条件に含まれるため、入力欄も合わせて空にする */
  const resetAll = () => {
    setSearchInput('');
    reset();
  };

  /**
   * 絞り込みを変えてもチェックした企業は保持する。
   * 「医療で 20 社選び、次に人材で 10 社足してリストにする」ような使い方ができるようにするため。
   * 選択件数は一括操作バーに出続けるので、見えない選択が残ることはない。
   */
  const onFilterChange = (key: string, value: string) => {
    setFilter(key as keyof typeof DEFAULT_FILTERS, value);
  };

  /**
   * 連絡先のクイック選択。個別条件（メール／フォーム／電話）と「いずれか」を
   * ひとつの Select にまとめる。細かい組み合わせは絞り込みシートで指定する。
   */
  const contactQuick =
    filters.hasAnyContact === 'true'
      ? 'any'
      : filters.hasAnyContact === 'false'
        ? 'none'
        : filters.hasForm === 'true'
          ? 'form'
          : filters.hasEmail === 'true'
            ? 'email'
            : filters.hasPhone === 'true'
              ? 'phone'
              : '';

  const onContactQuickChange = (value: string) => {
    setFilters({
      hasAnyContact: value === 'any' ? 'true' : value === 'none' ? 'false' : '',
      hasForm: value === 'form' ? 'true' : '',
      hasEmail: value === 'email' ? 'true' : '',
      hasPhone: value === 'phone' ? 'true' : '',
    });
  };

  /**
   * PC の表。列は 8 つに抑え、地域は企業名の下に添える。
   * 値が無いセルは「—」で揃え、文字で埋めない（未割当・未実施の行が大半でも一覧が騒がしくならないように）。
   */
  const columns: Column<CompanySummary>[] = [
    {
      key: 'name',
      header: '企業名',
      sortable: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground" title={row.name}>
            {row.name}
          </p>
          <p className="truncate text-note text-foreground-subtle">
            {[row.domain, [row.prefecture, row.city].filter(Boolean).join('')]
              .filter(Boolean)
              .join('・') || '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: '状況',
      sortable: true,
      width: '112px',
      render: (row) => <StatusBadge kind="company_status" value={row.status} />,
    },
    {
      key: 'industry',
      header: '業種',
      width: '160px',
      render: (row) => (
        <span className="flex min-w-0 items-center gap-1" title={row.industry ?? undefined}>
          <span className="truncate">{row.industry ?? <Muted>—</Muted>}</span>
          {/* 推定値（確度が低い）だけ印を付ける。取込・抽出で付く「中」は一覧では出さない */}
          {row.industryConfidence === 'low' && <ConfidenceMark level="low" />}
        </span>
      ),
    },
    {
      // 「誰が・いつ」は 1 列にまとめる（並び替えは最終接触日）
      key: 'lastContactedAt',
      header: '担当・最終接触',
      sortable: true,
      width: '116px',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate">
            {row.assignee?.name ?? <Muted title={messages.common.unassigned}>—</Muted>}
          </p>
          <p className="tabular text-note text-foreground-subtle">
            {row.lastContactedAt ? formatShortDate(row.lastContactedAt) : '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'contacts',
      header: '連絡先',
      width: '92px',
      render: (row) => <ContactIcons company={row} />,
    },
    {
      key: 'security',
      header: 'セキュリティ',
      width: '100px',
      render: (row) =>
        row.securityRisk ? (
          <StatusBadge
            kind="security_risk"
            value={row.securityRisk}
            emphasis={row.securityRisk === 'high' ? 'high' : undefined}
          />
        ) : (
          <Muted title="セキュリティチェック未実施">—</Muted>
        ),
    },
    {
      key: 'tags',
      header: 'タグ',
      width: '300px',
      // 業種欄と同じ名前のタグは重複なので出さない
      render: (row) => <TagChips tags={row.tags.filter((t) => t.name !== row.industry)} />,
    },
  ];

  const appliedChips = [
    filters.status && {
      key: 'status',
      label: `状況=${(filters.status as string).split(',').map((s) => CompanyStatuses.label(s)).join('・')}`,
    },
    filters.assigneeId && {
      key: 'assigneeId',
      label: `担当=${
        filters.assigneeId === 'unassigned'
          ? messages.common.unassigned
          : (assignees?.data.find((u) => u.id === filters.assigneeId)?.name ?? '—')
      }`,
    },
    filters.prefecture && { key: 'prefecture', label: `地域=${filters.prefecture}` },
    filters.industry && { key: 'industry', label: `業種=${filters.industry}` },
    filters.hasEmail && {
      key: 'hasEmail',
      label: `メール${filters.hasEmail === 'true' ? 'あり' : 'なし'}`,
    },
    filters.hasForm && {
      key: 'hasForm',
      label: `フォーム${filters.hasForm === 'true' ? 'あり' : 'なし'}`,
    },
    filters.hasPhone && {
      key: 'hasPhone',
      label: `電話${filters.hasPhone === 'true' ? 'あり' : 'なし'}`,
    },
    filters.hasAnyContact && {
      key: 'hasAnyContact',
      label: filters.hasAnyContact === 'true' ? '連絡手段あり' : '連絡手段なし',
    },
    filters.securityRisk && {
      key: 'securityRisk',
      label: `セキュリティ=${(filters.securityRisk as string)
        .split(',')
        .map((v) => SecurityRiskLevels.label(v))
        .join('・')}`,
    },
    filters.securityChecked && {
      key: 'securityChecked',
      label: filters.securityChecked === 'true' ? 'チェック実施済み' : 'チェック未実施',
    },
    filters.excluded === 'true' && { key: 'excluded', label: '対象外のみ' },
    filters.lowConfidenceOnly && { key: 'lowConfidenceOnly', label: '確度が低い項目を含む' },
    filters.listId && {
      key: 'listId',
      label: 'リストで絞り込み中',
    },
  ].filter(Boolean) as { key: string; label: string }[];

  const hasFilters = appliedChips.length > 0 || !!debouncedSearch;

  return (
    <>
      <PageHeader
        title={messages.company.title}
        description={
          totalCount != null ? (
            <span className="tabular">
              {formatNumber(totalCount)} 件中 {formatNumber(rows.length)} 件
            </span>
          ) : undefined
        }
        actions={
          <>
            {/* CSV の出入力は PC の作業なので、スマホでは主要操作だけ残す */}
            <Button
              variant="outline"
              onClick={() => exportCsv.mutate()}
              disabled={exportCsv.isPending}
              className="hidden lg:inline-flex"
            >
              <Download aria-hidden />
              {messages.common.export}
            </Button>
            <Button variant="outline" onClick={() => setCampaignListOpen(true)}>
              <ListPlus aria-hidden />
              {messages.list.createForCampaign}
            </Button>
            {hasRole('manager') && (
              <Button
                variant="outline"
                onClick={() => setImportOpen(true)}
                className="hidden lg:inline-flex"
              >
                <Upload aria-hidden />
                {messages.common.import}
              </Button>
            )}
            <Button onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden />
              {messages.company.add}
            </Button>
          </>
        }
      />

      <div className="mb-3 space-y-2">
        <Input
          type="search"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
          }}
          placeholder="社名・ドメイン・電話で検索"
          aria-label="企業を検索"
        />

        <FilterChips>
          <Button variant="outline" size="sm" onClick={() => setFilterOpen(true)} className="shrink-0">
            <Filter aria-hidden className="size-4" />
            {activeCount > 0 ? messages.common.filterCount(activeCount) : messages.common.filter}
          </Button>
          <Select
            value={filters.status as string}
            onChange={(e) => onFilterChange('status', e.target.value)}
            aria-label="営業状況で絞り込む"
            className="h-8 w-auto shrink-0 text-note lg:h-8"
          >
            <option value="">状況：すべて</option>
            {CompanyStatuses.list.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </Select>
          <Select
            value={filters.assigneeId as string}
            onChange={(e) => onFilterChange('assigneeId', e.target.value)}
            aria-label="担当者で絞り込む"
            className="h-8 w-auto shrink-0 text-note lg:h-8"
          >
            <option value="">担当：すべて</option>
            <option value="unassigned">{messages.common.unassigned}</option>
            {assignees?.data.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name ?? user.email}
              </option>
            ))}
          </Select>
          <Select
            value={filters.tagIds as string}
            onChange={(e) => onFilterChange('tagIds', e.target.value)}
            aria-label="タグで絞り込む"
            className="h-8 w-auto shrink-0 text-note lg:h-8"
          >
            <option value="">タグ：すべて</option>
            {tags?.data.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </Select>
          <Select
            value={contactQuick}
            onChange={(e) => onContactQuickChange(e.target.value)}
            aria-label="連絡先で絞り込む"
            className="h-8 w-auto shrink-0 text-note lg:h-8"
          >
            <option value="">連絡先：すべて</option>
            <option value="any">連絡手段あり</option>
            <option value="none">連絡手段なし</option>
            <option value="form">フォームあり</option>
            <option value="email">メールあり</option>
            <option value="phone">電話あり</option>
          </Select>
          <Select
            value={filters.securityRisk as string}
            onChange={(e) => onFilterChange('securityRisk', e.target.value)}
            aria-label="セキュリティの判定で絞り込む"
            className="h-8 w-auto shrink-0 text-note lg:h-8"
          >
            <option value="">セキュリティ：すべて</option>
            {/* 絞り込みシートで複数選んだ状態は、ここでは選択肢にできないので現状だけ示す */}
            {(filters.securityRisk as string).includes(',') && (
              <option value={filters.securityRisk as string}>
                {(filters.securityRisk as string)
                  .split(',')
                  .map((v) => SecurityRiskLevels.label(v))
                  .join('・')}
              </option>
            )}
            {SecurityRiskLevels.list.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </Select>
        </FilterChips>

        {appliedChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-note text-foreground-muted">適用中:</span>
            {appliedChips.map((chip) => (
              <AppliedChip
                key={chip.key}
                label={chip.label}
                onRemove={() => onFilterChange(chip.key, '')}
              />
            ))}
            <Button variant="ghost" size="sm" onClick={resetAll}>
              {messages.common.reset}
            </Button>
          </div>
        )}
      </div>

      <ResponsiveList
        columns={columns}
        rows={rows}
        layout="fixed"
        isLoading={isLoading}
        selection={{
          selected: selection.selected,
          onToggle: selection.toggle,
          onToggleAll: (checked) => selection.toggleAll(rows.map((r) => r.id), checked),
        }}
        onRowClick={(row) => navigate(`/companies/${row.id}`)}
        sort={{
          field: (filters.sort as string).replace(/^-/, ''),
          direction: (filters.sort as string).startsWith('-') ? 'desc' : 'asc',
        }}
        onSortChange={(field) => {
          const current = filters.sort as string;
          onFilterChange('sort', current === `-${field}` ? field : `-${field}`);
        }}
        hasMore={hasNextPage}
        onLoadMore={() => void fetchNextPage()}
        isLoadingMore={isFetchingNextPage}
        empty={
          hasFilters ? (
            <EmptyState
              title={messages.empty.companiesFiltered.title}
              description={messages.empty.companiesFiltered.description}
              action={{ label: messages.empty.companiesFiltered.action, onClick: reset }}
              icon={<Building2 className="size-8" />}
            />
          ) : (
            <EmptyState
              title={messages.empty.companies.title}
              description={messages.empty.companies.description}
              icon={<Building2 className="size-8" />}
            />
          )
        }
        card={(row) => (
          <button
            type="button"
            className="w-full text-left"
            onClick={() => navigate(`/companies/${row.id}`)}
          >
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 truncate text-body font-semibold">{row.name}</span>
                  <StatusBadge kind="company_status" value={row.status} size="lg" />
                </div>
                <p className="truncate text-note text-foreground-muted">
                  {[row.industry, [row.prefecture, row.city].filter(Boolean).join('')]
                    .filter(Boolean)
                    .join('・') || '—'}
                  {row.industryConfidence === 'low' && <ConfidenceMark level="low" className="ml-1.5" />}
                </p>
                <p className="text-note text-foreground-muted">
                  最終接触 <span className="tabular">{formatShortDate(row.lastContactedAt)}</span>
                  {' ・ '}
                  担当 {row.assignee?.name ?? '—'}
                </p>
                {row.securityRisk && (
                  <p className="flex items-center gap-1 text-note text-foreground-muted">
                    <Shield aria-hidden className="size-3.5" />
                    セキュリティチェック: {formatShortDate(row.securityCheckedAt)} の結果あり
                  </p>
                )}
                <ContactIcons company={row} showLabels />
              </div>
              <RowChevron />
            </div>
          </button>
        )}
      />

      <BulkBar count={selection.count} onClear={selection.clear}>
        <Button variant="outline" size="sm" onClick={() => setAddToListOpen(true)}>
          <ListPlus aria-hidden className="size-4" />
          {messages.list.addToList}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setCampaignListOpen(true)}>
          <ListPlus aria-hidden className="size-4" />
          {messages.list.createForCampaign}
        </Button>
        {hasRole('manager') && (
          <Button variant="outline" size="sm" onClick={() => setBulkDialog('assign')}>
            担当割当
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => setBulkDialog('tags')}>
          タグ
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => bulkSecurityScan.mutate([...selection.selected])}
          disabled={bulkSecurityScan.isPending}
          title={messages.company.securityScanNote}
        >
          <Shield aria-hidden className="size-4" />
          {messages.company.securityScan}
        </Button>
        {hasRole('manager') && (
          <Button variant="outline" size="sm" onClick={() => setBulkDialog('exclude')}>
            対象外
          </Button>
        )}
      </BulkBar>

      <CompanyFilterSheet
        open={filterOpen}
        onOpenChange={setFilterOpen}
        filters={filters}
        onChange={onFilterChange}
        onReset={resetAll}
        assignees={assignees?.data ?? []}
        tags={tags?.data ?? []}
      />

      <CompanyCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => navigate(`/companies/${id}`)}
      />

      <CsvImportDialog open={importOpen} onOpenChange={setImportOpen} />

      <AddToListDialog
        open={addToListOpen}
        onOpenChange={setAddToListOpen}
        companyIds={[...selection.selected]}
        onDone={selection.clear}
      />

      <CampaignListDialog
        open={campaignListOpen}
        onOpenChange={setCampaignListOpen}
        companyIds={[...selection.selected]}
        filter={filterForList}
        filteredCount={totalCount ?? undefined}
        onDone={selection.clear}
      />

      <BulkAssignDialog
        open={bulkDialog === 'assign'}
        onOpenChange={(open) => setBulkDialog(open ? 'assign' : null)}
        companyIds={[...selection.selected]}
        assignees={assignees?.data ?? []}
        onDone={() => {
          selection.clear();
          void queryClient.invalidateQueries({ queryKey: ['companies'] });
        }}
      />
      <BulkTagDialog
        open={bulkDialog === 'tags'}
        onOpenChange={(open) => setBulkDialog(open ? 'tags' : null)}
        companyIds={[...selection.selected]}
        tags={tags?.data ?? []}
        onDone={() => {
          selection.clear();
          void queryClient.invalidateQueries({ queryKey: ['companies'] });
        }}
      />
      <BulkExcludeDialog
        open={bulkDialog === 'exclude'}
        onOpenChange={(open) => setBulkDialog(open ? 'exclude' : null)}
        companyIds={[...selection.selected]}
        onDone={() => {
          selection.clear();
          void queryClient.invalidateQueries({ queryKey: ['companies'] });
        }}
      />
    </>
  );
};

/** 値が無いセル。文字で埋めずに「—」で揃える */
const Muted = ({ children, title }: { children: ReactNode; title?: string }) => (
  <span className="text-foreground-subtle" title={title}>
    {children}
  </span>
);

/** タグは 2 つまで見せ、残りは件数にまとめる（全件はツールチップ） */
const TagChips = ({ tags }: { tags: { id: string; name: string }[] }) => {
  if (tags.length === 0) return <Muted>—</Muted>;
  const shown = tags.slice(0, 2);
  const rest = tags.length - shown.length;
  return (
    <span className="flex min-w-0 items-center gap-1" title={tags.map((t) => t.name).join(' / ')}>
      {shown.map((tag) => (
        <Badge key={tag.id} variant="muted" className="min-w-0 max-w-40">
          <span className="truncate">{tag.name}</span>
        </Badge>
      ))}
      {rest > 0 && <span className="shrink-0 text-note text-foreground-muted">+{rest}</span>}
    </span>
  );
};
